import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useSearchParams } from "react-router-dom";
import {
  FiBarChart2,
  FiGitMerge,
  FiGrid,
  FiLogOut,
  FiMapPin,
  FiSettings,
  FiTrendingUp,
} from "react-icons/fi";
import Navbar from "../Components/Navbar";
import { usePmsTheme } from "../Components/PmsThemeContext";
import useIsMobile from "../hooks/useIsMobile";
import axios from "../api/axiosInstance";
import { readStoredUser, setActiveBusinessUnitSession } from "../utils/sessionUser";

export default function AnalyticsPanel() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const { language } = usePmsTheme();
  const commonCopy = language.messages.common;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarPinned, setIsSidebarPinned] = useState(false);
  const [businessUnitName, setBusinessUnitName] = useState("");
  const [sessionUser, setSessionUser] = useState(() => readStoredUser());

  const currentUser = sessionUser || {};
  const businessUnitId =
    searchParams.get("business_unit_id") || currentUser?.active_business_unit_id || "1";

  const withBusinessUnit = useCallback(
    (path) => `${path}?business_unit_id=${encodeURIComponent(String(businessUnitId))}`,
    [businessUnitId]
  );

  const isSidebarExpanded = isSidebarPinned || isSidebarOpen;

  const handleLogout = useCallback(() => {
    localStorage.removeItem("palantir_token");
    localStorage.removeItem("palantir_user");
    window.location.href = "/";
  }, []);

  useEffect(() => {
    const syncSessionFromStorage = () => {
      setSessionUser(readStoredUser());
    };

    window.addEventListener("palantir-session-updated", syncSessionFromStorage);
    return () => {
      window.removeEventListener("palantir-session-updated", syncSessionFromStorage);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const syncActiveBusinessUnit = async () => {
      const requestedBu = Number(businessUnitId || 0);
      const activeBu = Number(currentUser?.active_business_unit_id || 0);

      if (!currentUser?.id || !requestedBu || requestedBu === activeBu) return;

      try {
        const nextUser = await setActiveBusinessUnitSession(currentUser.id, requestedBu);
        if (!cancelled) setSessionUser(nextUser);
      } catch (err) {
        if (!cancelled) console.warn("[AnalyticsPanel] Error syncing active business unit:", err);
      }
    };

    syncActiveBusinessUnit();

    return () => {
      cancelled = true;
    };
  }, [businessUnitId, currentUser?.active_business_unit_id, currentUser?.id]);

  useEffect(() => {
    let cancelled = false;

    const loadBusinessUnitName = async () => {
      if (!businessUnitId) return;
      try {
        const res = await axios.get(`/api/business-units/${businessUnitId}`);
        if (!cancelled) setBusinessUnitName(String(res?.data?.name || "").trim());
      } catch (err) {
        if (!cancelled) setBusinessUnitName("");
        console.warn("[AnalyticsPanel] Error loading business unit name:", err);
      }
    };

    loadBusinessUnitName();

    return () => {
      cancelled = true;
    };
  }, [businessUnitId]);

  const menuItems = useMemo(
    () => [
      {
        key: "sales",
        label: "Sales",
        meta: "Net by family",
        to: withBusinessUnit("/analytics/sales"),
        Icon: FiBarChart2,
      },
      {
        key: "cost-control",
        label: "Cost control",
        meta: "Daily margin",
        to: withBusinessUnit("/analytics/cost-control"),
        Icon: FiGitMerge,
      },
      {
        key: "forecasting",
        label: "Forecasting",
        meta: "Scenarios",
        to: withBusinessUnit("/analytics/forecasting"),
        Icon: FiTrendingUp,
      },
      {
        key: "settings",
        label: "Settings",
        meta: "Mappings",
        to: withBusinessUnit("/analytics/settings"),
        Icon: FiSettings,
      },
    ],
    [withBusinessUnit]
  );

  if (isMobile) {
    return (
      <div className="analyticsModule posModule posModuleMobile">
        <Navbar />
        <main className="pmsMain pmsMain--mobile" style={{ paddingTop: "64px", paddingLeft: 8, paddingRight: 8 }}>
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="analyticsModule posModule pmsShell">
      <aside
        className={`pmsSidebar ${isSidebarExpanded ? "pmsSidebar--open" : ""} ${
          isSidebarPinned ? "pmsSidebar--pinned" : ""
        }`}
        aria-label="Analytics navigation"
        onMouseEnter={() => setIsSidebarOpen(true)}
        onMouseLeave={() => {
          if (!isSidebarPinned) setIsSidebarOpen(false);
        }}
      >
        <div className="pmsSidebarInner">
          <div className="pmsBrand" title="Control de Gestion">
            <div className="pmsBrandMark">
              <img
                className="pmsBrandLogo"
                src={`${process.env.PUBLIC_URL}/Isotipo_Pilcore_Blanco.svg`}
                alt="Pilcore"
              />
            </div>
            <div className="pmsBrandText">
              <div className="pmsBrandTitle">Control</div>
              <div className="pmsBrandSubtitle">Pilcore</div>
              <div
                className="pmsBrandHotel"
                title={businessUnitName || currentUser?.business_unit_name || commonCopy.businessUnit}
              >
                {businessUnitName || currentUser?.business_unit_name || commonCopy.businessUnit}
              </div>
              <div className="pmsBrandActions">
                <span className="pmsBrandLogoutIcon" aria-hidden="true">
                  <FiLogOut size={11} />
                </span>
                <button
                  type="button"
                  className="pmsBrandLogout"
                  onClick={handleLogout}
                  title={commonCopy.logout}
                >
                  {commonCopy.logout}
                </button>
              </div>
            </div>
            {isSidebarExpanded ? (
              <button
                type="button"
                className={`pmsPinButton ${isSidebarPinned ? "pmsPinButton--active" : ""}`}
                onClick={() => {
                  setIsSidebarPinned((prev) => {
                    const next = !prev;
                    setIsSidebarOpen(next);
                    return next;
                  });
                }}
                title={isSidebarPinned ? "Unpin sidebar" : "Pin sidebar"}
                aria-pressed={isSidebarPinned}
                aria-label={isSidebarPinned ? "Unpin sidebar" : "Pin sidebar"}
              >
                <FiMapPin size={14} />
              </button>
            ) : null}
          </div>

          <nav className="pmsNav">
            {menuItems.map((item) => {
              const Icon = item.Icon || FiGrid;
              const targetPath = String(item.to || "").split("?")[0];
              const isActive =
                location.pathname === targetPath || location.pathname.startsWith(`${targetPath}/`);

              return (
                <NavLink
                  key={item.key}
                  to={item.to}
                  title={item.label}
                  className={() => `pmsNavItem ${isActive ? "pmsNavItem--active" : ""}`}
                >
                  <span className="pmsNavItemIcon" aria-hidden="true">
                    <Icon size={18} />
                  </span>
                  <span className="pmsNavItemText">
                    <span className="pmsNavItemLabel">{item.label}</span>
                    <span className="pmsNavItemMeta">{item.meta}</span>
                  </span>
                </NavLink>
              );
            })}
          </nav>
        </div>
      </aside>

      <main className="pmsMain">
        <Outlet />
      </main>
    </div>
  );
}
